import React, { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import { getApiBaseUrl } from '../../utils/getApiUrl';
import { AppointmentAmiCall, CallLog } from '../../types';

const CALL_OUTCOME_LABELS: Record<CallLog['outcome'], string> = {
  no_answer: 'بدون پاسخ',
  busy: 'مشغول',
  unreachable: 'در دسترس نیست',
  answered: 'پاسخ داده شد',
  wrong_number: 'شماره اشتباه',
};

function formatCallDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins <= 0) return `${secs} ثانیه`;
  return `${mins} دقیقه و ${secs} ثانیه`;
}

function formatCallDateParts(value: string): { dateLabel: string; timeLabel: string } {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { dateLabel: '—', timeLabel: '—' };
  }
  return {
    dateLabel: parsed.toLocaleDateString('fa-IR', { year: 'numeric', month: 'short', day: 'numeric' }),
    timeLabel: parsed.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  };
}

function getCallStatusVisuals(connected: boolean) {
  if (connected) {
    return {
      stripe: 'bg-emerald-500',
      iconWrap: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
      cardBorder: 'border-emerald-100/90',
      progressBar: 'bg-emerald-500',
    };
  }
  return {
    stripe: 'bg-rose-500',
    iconWrap: 'bg-rose-50 text-rose-600 ring-rose-100',
    cardBorder: 'border-rose-100/90',
    progressBar: 'bg-rose-500',
  };
}

function InboundCallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 7L7 17M7 17H15M7 17V9" />
    </svg>
  );
}

function OutboundCallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7H9M17 7V15" />
    </svg>
  );
}

function PhoneCallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
      />
    </svg>
  );
}

type CallHistoryEntry =
  | { kind: 'manual'; sortAt: number; data: CallLog }
  | { kind: 'ami'; sortAt: number; data: AppointmentAmiCall };

interface AppointmentCallHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: number | null;
  appointmentLabel?: string;
}

const AppointmentCallHistoryModal: React.FC<AppointmentCallHistoryModalProps> = ({
  isOpen,
  onClose,
  appointmentId,
  appointmentLabel,
}) => {
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [amiCalls, setAmiCalls] = useState<AppointmentAmiCall[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCallHistory = useCallback(async () => {
    if (!appointmentId) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('admin_token');
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/admin/appointments/${appointmentId}/call-history`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('خطا در دریافت تاریخچه تماس');
      }

      const data = await response.json();
      setCallLogs(Array.isArray(data?.manual_logs) ? data.manual_logs : []);
      setAmiCalls(Array.isArray(data?.ami_calls) ? data.ami_calls : []);
    } catch (err) {
      setCallLogs([]);
      setAmiCalls([]);
      setError(err instanceof Error ? err.message : 'خطا در دریافت تاریخچه تماس');
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    if (isOpen && appointmentId) {
      void fetchCallHistory();
    } else if (!isOpen) {
      setCallLogs([]);
      setAmiCalls([]);
      setError(null);
    }
  }, [isOpen, appointmentId, fetchCallHistory]);

  const callHistoryEntries = useMemo<CallHistoryEntry[]>(() => {
    const manualEntries: CallHistoryEntry[] = callLogs.map((log) => ({
      kind: 'manual',
      sortAt: new Date(log.created_at).getTime(),
      data: log,
    }));
    const amiEntries: CallHistoryEntry[] = amiCalls.map((call) => ({
      kind: 'ami',
      sortAt: new Date(call.call_time).getTime() || 0,
      data: call,
    }));
    return [...manualEntries, ...amiEntries].sort((a, b) => b.sortAt - a.sortAt);
  }, [callLogs, amiCalls]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      style={{ direction: 'rtl' }}
    >
      <div
        className="bg-white w-full max-w-lg rounded-xl shadow-2xl border border-gray-200 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between shrink-0 bg-gradient-to-l from-indigo-50/80 to-white rounded-t-xl">
          <div>
            <h3 className="text-lg font-bold text-gray-900">تاریخچه تماس</h3>
            {appointmentLabel && (
              <p className="text-sm text-gray-500 mt-0.5">{appointmentLabel}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
            aria-label="بستن"
          >
            ×
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner text="در حال بارگذاری تاریخچه..." />
            </div>
          ) : error ? (
            <p className="text-center text-red-600 py-8 text-sm">{error}</p>
          ) : callHistoryEntries.length === 0 ? (
            <div className="p-8 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <PhoneCallIcon />
              </div>
              <p className="text-sm font-medium text-slate-600">تماسی یافت نشد</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {callHistoryEntries.map((entry) =>
                entry.kind === 'manual' ? (
                  (() => {
                    const { dateLabel, timeLabel } = formatCallDateParts(entry.data.created_at);
                    return (
                      <li
                        key={`manual-${entry.data.id}`}
                        className="rounded-xl border border-dashed border-amber-200 bg-amber-50/60 px-3.5 py-3 shadow-sm"
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 min-w-0 flex-wrap">
                            <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                              لاگ تماس
                            </span>
                            <span className="text-xs font-medium text-amber-900/80 truncate">
                              {entry.data.admin_username}
                            </span>
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-white text-amber-900 ring-1 ring-amber-200">
                              {CALL_OUTCOME_LABELS[entry.data.outcome] || entry.data.outcome}
                            </span>
                          </div>
                          <div className="text-left shrink-0">
                            <p className="text-[11px] font-medium text-amber-900/70 tabular-nums">{timeLabel}</p>
                            <p className="text-[10px] text-amber-800/50 tabular-nums">{dateLabel}</p>
                          </div>
                        </div>
                        <p className="text-sm text-slate-800 leading-6 whitespace-pre-wrap break-words">
                          {entry.data.notes?.trim() || '—'}
                        </p>
                        {entry.data.follow_up_at && (
                          <p className="text-xs text-amber-800/70 mt-2">
                            پیگیری:{' '}
                            {new Date(entry.data.follow_up_at).toLocaleString('fa-IR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        )}
                      </li>
                    );
                  })()
                ) : (
                  (() => {
                    const call = entry.data;
                    const visuals = getCallStatusVisuals(call.answered);
                    const { dateLabel, timeLabel } = formatCallDateParts(call.call_time);
                    const DirectionIcon =
                      call.direction === 'inbound'
                        ? InboundCallIcon
                        : call.direction === 'outbound'
                          ? OutboundCallIcon
                          : PhoneCallIcon;
                    const durationPct = Math.min(100, Math.round((call.duration_seconds / 300) * 100));
                    return (
                      <li
                        key={`ami-${call.unique_id || call.call_time}`}
                        className={`relative overflow-hidden rounded-2xl border bg-white shadow-sm ${visuals.cardBorder}`}
                      >
                        <div className={`absolute right-0 top-0 bottom-0 w-1 ${visuals.stripe}`} />
                        <div className="p-3.5 pr-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2.5 min-w-0">
                              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${visuals.iconWrap}`}>
                                <DirectionIcon />
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-slate-900 truncate">{call.agent_label}</p>
                                <p className="text-[11px] font-medium mt-0.5 text-slate-500">
                                  {call.direction_label} · مرکز تلفن
                                </p>
                              </div>
                            </div>
                            <div className="text-left shrink-0">
                              <p className="text-xs font-semibold text-slate-700 tabular-nums">{timeLabel}</p>
                              <p className="text-[10px] text-slate-400 tabular-nums">{dateLabel}</p>
                            </div>
                          </div>

                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ${
                                call.answered
                                  ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
                                  : 'bg-rose-50 text-rose-700 ring-rose-100'
                              }`}
                            >
                              {call.disposition_label}
                            </span>
                            <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium bg-slate-100 text-slate-600 ring-1 ring-slate-200">
                              {call.direction_label}
                            </span>
                          </div>

                          {call.answered && (
                            <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5">
                              <div className="flex items-center justify-between gap-2 text-xs text-slate-600">
                                <span>مدت مکالمه</span>
                                <span className="font-semibold text-slate-800 tabular-nums">
                                  {formatCallDuration(call.duration_seconds)}
                                </span>
                              </div>
                              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200">
                                <div
                                  className={`h-full rounded-full ${visuals.progressBar}`}
                                  style={{ width: `${Math.max(durationPct, 8)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })()
                ),
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppointmentCallHistoryModal;
